import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchServers, fetchLogs } from '../api/admin';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer, Tooltip, Snackbar } from '@mui/material';
import { MachineActionsManager } from '../components/MachineActionsManager';

export function ServersPage() {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedServer, setSelectedServer] = useState<any>(null);
    const [openMenuServerId, setOpenMenuServerId] = useState<string | null>(null);

    // Dialog & Snackbar States
    const [actionServer, setActionServer] = useState<any>(null);
    const [actionType, setActionType] = useState<'block' | 'unblock' | 'deactivate' | 'replace' | 'reactivate' | null>(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [statusFilter, setStatusFilter] = useState<string>(() => {
        return localStorage.getItem('mighty-licence-server:machine-status-filter') || 'all';
    });

    const handleStatusFilterChange = (val: string) => {
        setStatusFilter(val);
        localStorage.setItem('mighty-licence-server:machine-status-filter', val);
        setCurrentPage(1);
    };

    // Queries
    const { data: serversData, isLoading: isLoadingServers } = useQuery({
        queryKey: ['servers'],
        queryFn: fetchServers,
    });

    const { data: logsData } = useQuery({
        queryKey: ['logs'],
        queryFn: () => fetchLogs(),
        enabled: !!selectedServer
    });

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    if (isLoadingServers) {
        return <div className="text-center py-8">Loading machines...</div>;
    }

    const servers = serversData?.servers || [];
    const serverLogs = (logsData?.logs || []).filter((l: any) => l.serverId === selectedServer?.serverId);

    // Client-side search matching licenseKey, serverName, fingerprint, ipAddress, and status
    const filteredServers = servers.filter((server: any) => {
        if (statusFilter !== 'all' && server.status !== statusFilter) {
            return false;
        }
        const query = searchQuery.toLowerCase();
        return (
            (server.serverName && server.serverName.toLowerCase().includes(query)) ||
            (server.licenseKey && server.licenseKey.toLowerCase().includes(query)) ||
            (server.fingerprint && server.fingerprint.toLowerCase().includes(query)) ||
            (server.ipAddress && server.ipAddress.toLowerCase().includes(query))
        );
    });

    const itemsPerPage = 25;
    const totalPages = Math.ceil(filteredServers.length / itemsPerPage);
    const paginatedServers = filteredServers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Format short fingerprint
    const getShortFingerprint = (fp: string) => {
        if (!fp) return '-';
        if (fp.length <= 16) return fp;
        return `${fp.substring(0, 8)}....${fp.substring(fp.length - 8)}`;
    };

    return (
        <div>
            <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Machines
                </h1>
                
                {/* Search bar */}
                <div className="w-full sm:w-80">
                    <input
                        placeholder="Search key, name, fingerprint, IP..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Activated Machines
                    </h2>
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => handleStatusFilterChange(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
                        >
                            <option value="all">All Machines</option>
                            <option value="active">Active</option>
                            <option value="blocked">Blocked</option>
                            <option value="deactivated">Deactivated</option>
                            <option value="suspicious">Suspicious</option>
                            <option value="migrated">Migrated</option>
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Machine Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    License Key
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Fingerprint (short)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    IP Address
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Version
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Activated Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Last Heartbeat
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Reactivations
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedServers.map((server: any) => (
                                <tr 
                                    key={server.id} 
                                    onClick={() => setSelectedServer(server)}
                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-all"
                                >
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-medium">
                                        {server.serverName}
                                    </td>
                                    <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                        {server.licenseKey}
                                    </td>
                                    <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap no-row-click">
                                        <div className="flex items-center gap-2">
                                            <span>{getShortFingerprint(server.fingerprint)}</span>
                                            {server.fingerprint && (
                                                <Tooltip title="Copy fingerprint" arrow>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(server.fingerprint);
                                                            showMessage('Fingerprint copied');
                                                        }}
                                                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-slate-600"
                                                    >
                                                        <ContentCopyIcon className="!w-3.5 !h-3.5" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                        {server.ipAddress || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-mono">
                                        {server.version}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                        {server.activatedAt ? new Date(server.activatedAt).toLocaleDateString() : (server.installationDate ? new Date(server.installationDate).toLocaleDateString() : 'N/A')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                                            server.status === 'active'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : server.status === 'suspicious'
                                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    : server.status === 'blocked'
                                                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                        }`}>
                                            {server.status || 'offline'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                        {server.lastHeartbeat ? new Date(server.lastHeartbeat).toLocaleString() : 'Never'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-semibold">
                                        {server.reactivationCount || 0}
                                        {server.reactivationCount > 3 && (
                                            <span className="ml-1 inline-flex items-center px-1.5 py-0.2 rounded text-[10px] bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" title="Excessive reactivation limit warning">
                                                ⚠️
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm whitespace-nowrap no-row-click relative">
                                        {server.status === 'migrated' ? (
                                            <Tooltip title="Migrated machines cannot be modified" arrow>
                                                <span className="inline-block">
                                                    <button
                                                        disabled
                                                        className="p-1 text-slate-300 dark:text-slate-600 cursor-not-allowed"
                                                    >
                                                        <MoreVertIcon className="!w-4 !h-4" />
                                                    </button>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenMenuServerId(openMenuServerId === server.serverId ? null : server.serverId);
                                                    }}
                                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 hover:text-slate-700"
                                                >
                                                    <MoreVertIcon className="!w-4 !h-4" />
                                                </button>
                                                
                                                {openMenuServerId === server.serverId && (
                                                    <div className="absolute right-6 mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-150 dark:border-gray-700 z-50 py-1 text-left">
                                                        {server.status === 'deactivated' && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('reactivate');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold cursor-pointer"
                                                                >
                                                                    <CheckIcon className="!w-4 !h-4 text-green-600 dark:text-green-400" />
                                                                    Re-activate Machine
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('replace');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-[#c87a53] hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                >
                                                                    Replace Machine
                                                                </button>
                                                            </>
                                                        )}

                                                        {server.status === 'blocked' && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('unblock');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                >
                                                                    Unblock Machine
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('deactivate');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                                >
                                                                    Deactivate
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('replace');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-[#c87a53] hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                >
                                                                    Replace Machine
                                                                </button>
                                                            </>
                                                        )}

                                                        {(server.status === 'active' || server.status === 'suspicious') && (
                                                            <>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('block');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-red-650 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                >
                                                                    Block Machine
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('deactivate');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                                >
                                                                    Deactivate
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setActionServer(server);
                                                                        setActionType('replace');
                                                                        setOpenMenuServerId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-2 text-sm text-[#c87a53] hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                >
                                                                    Replace Machine
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {paginatedServers.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                                        No active machines found matching filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-4 px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-750 rounded-b-3xl">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Showing <span className="font-semibold text-gray-800 dark:text-gray-250">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                            <span className="font-semibold text-gray-800 dark:text-gray-250">{Math.min(currentPage * itemsPerPage, filteredServers.length)}</span> of{' '}
                            <span className="font-semibold text-gray-800 dark:text-gray-250">{filteredServers.length}</span> machines
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors"
                            >
                                Prev
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Server Details Drawer */}
            <Drawer
                anchor="right"
                open={!!selectedServer}
                onClose={() => setSelectedServer(null)}
                PaperProps={{
                    className: 'w-[450px] max-w-full p-6 dark:bg-gray-800 dark:text-white border-l border-gray-200 dark:border-gray-700 flex flex-col justify-between'
                }}
            >
                {selectedServer && (
                    <div className="space-y-6 flex-grow overflow-y-auto pr-1">
                        <div className="flex justify-between items-center border-b pb-4 border-gray-200 dark:border-gray-700">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Machine Details</h2>
                                <p className="text-xs text-gray-500">{selectedServer.serverId}</p>
                            </div>
                            <button onClick={() => setSelectedServer(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Machine Name</p>
                                <p className="font-semibold text-gray-800 dark:text-gray-200">{selectedServer.serverName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Status</p>
                                <span className={`inline-block px-2 py-0.5 mt-1 text-xs rounded-full ${
                                    selectedServer.status === 'active' 
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                                        : selectedServer.status === 'suspicious'
                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                                            : selectedServer.status === 'blocked'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700/40 dark:text-gray-200'
                                }`}>
                                    {selectedServer.status}
                                </span>
                            </div>
                            <div className="col-span-2">
                                <p className="text-xs text-gray-400 font-medium">License Key</p>
                                <p className="font-mono text-gray-800 dark:text-gray-200">{selectedServer.licenseKey}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-xs text-gray-400 font-medium">Fingerprint</p>
                                <div className="flex items-center gap-2 font-mono text-xs text-gray-700 dark:text-gray-300 bg-slate-50 dark:bg-slate-900/40 p-2 rounded border dark:border-gray-700">
                                    <span className="break-all">{selectedServer.fingerprint}</span>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(selectedServer.fingerprint);
                                            showMessage('Fingerprint copied');
                                        }}
                                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-500"
                                        title="Copy fingerprint"
                                    >
                                        <ContentCopyIcon className="!w-3.5 !h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">IP Address</p>
                                <p className="text-gray-800 dark:text-gray-200">{selectedServer.ipAddress || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Version</p>
                                <p className="text-gray-800 dark:text-gray-200">{selectedServer.version}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Reactivations</p>
                                <p className="text-gray-800 dark:text-gray-200">{selectedServer.reactivationCount || 0}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Activated Date</p>
                                <p className="text-gray-800 dark:text-gray-200">
                                    {selectedServer.activatedAt ? new Date(selectedServer.activatedAt).toLocaleDateString() : (selectedServer.installationDate ? new Date(selectedServer.installationDate).toLocaleDateString() : 'N/A')}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Last Seen</p>
                                <p className="text-gray-800 dark:text-gray-200">{selectedServer.lastSeenAt ? new Date(selectedServer.lastSeenAt).toLocaleString() : 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Last Heartbeat</p>
                                <p className="text-gray-800 dark:text-gray-200">{selectedServer.lastHeartbeat ? new Date(selectedServer.lastHeartbeat).toLocaleString() : 'Never'}</p>
                            </div>
                            {selectedServer.status === 'blocked' && (
                                <div className="col-span-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 p-3 rounded-xl">
                                    <p className="text-xs text-red-500 font-semibold mb-1">Blocked Reason</p>
                                    <p className="text-sm text-red-700 dark:text-red-300">{selectedServer.blockedReason || 'No reason provided'}</p>
                                </div>
                            )}
                        </div>

                        <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Actions</h3>
                            <div className="flex flex-wrap gap-2">
                                {selectedServer.status !== 'blocked' ? (
                                    <button
                                        onClick={() => {
                                            setActionServer(selectedServer);
                                            setActionType('block');
                                        }}
                                        className="px-3 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-700 font-medium cursor-pointer"
                                    >
                                        Block Machine
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => {
                                            setActionServer(selectedServer);
                                            setActionType('unblock');
                                        }}
                                        className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 font-medium cursor-pointer"
                                    >
                                        Unblock Machine
                                    </button>
                                )}
                                {selectedServer.status !== 'deactivated' && (
                                    <button
                                        onClick={() => {
                                            setActionServer(selectedServer);
                                            setActionType('deactivate');
                                        }}
                                        className="px-3 py-1.5 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 font-medium cursor-pointer"
                                    >
                                        Deactivate
                                    </button>
                                )}
                                {selectedServer.status !== 'migrated' && (
                                    <button
                                        onClick={() => {
                                            setActionServer(selectedServer);
                                            setActionType('replace');
                                        }}
                                        className="px-3 py-1.5 bg-[#c87a53] text-white rounded text-xs hover:bg-[#b06742] font-medium cursor-pointer"
                                    >
                                        Replace Machine
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Audit Timeline</h3>
                            <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1 text-xs">
                                {serverLogs.length === 0 ? (
                                    <p className="text-gray-400">No logs for this machine.</p>
                                ) : (
                                    serverLogs.map((log: any) => (
                                        <div key={log.id} className="flex gap-2 border-l border-gray-300 dark:border-gray-700 pl-3 relative">
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 absolute -left-[4px] top-1"></div>
                                            <div className="flex-grow">
                                                <p className="font-semibold text-gray-700 dark:text-gray-300">{log.type}</p>
                                                <p className="text-[10px] text-gray-400">{log.details || 'No additional details'}</p>
                                                <p className="text-[10px] text-gray-400">{new Date(log.createdAt || log.timestamp).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Drawer>

            {/* Reusable Machine Actions Manager */}
            <MachineActionsManager
                server={actionServer}
                action={actionType}
                onClose={() => {
                    setActionServer(null);
                    setActionType(null);
                }}
                onSuccess={(msg) => {
                    showMessage(msg);
                    if (selectedServer) {
                        const refreshed = serversData?.servers?.find((s: any) => s.serverId === selectedServer.serverId);
                        if (refreshed) {
                            setSelectedServer(refreshed);
                        }
                    }
                }}
                onError={(err) => showMessage(err)}
            />

            {/* Snackbars */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </div>
    );
}